import { useState } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Button } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AddNewApplicationNavItem } from 'components/addNewApplication/AddNewApplicationNavItem';
import { useDashboard } from 'components/Dashboard/Dashboard.context';
import { HelpModal } from 'components/Navigation/Help/HelpModal';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import ConfusedGuyLookingAtHisLaptop from 'svg/ConfusedGuyLookingAtHisLaptop';
import GuyWithLaptopIcon from 'svg/GuyWithLaptopIcon';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { useApplicationsTableState } from '../Context/ApplicationsTableState.context';
import { TableLoader } from './TableLoader/TableLoader';
import styles from './TableStatus.module.css';

export const TableStatus = () => {
  const { table } = useApplicationsTableState();
  const { isLoading, state, setIsSettingsOpen } = useDashboard();
  const { introducer } = useIntroducerData();
  const [showHelpModal, setShowHelpModal] = useState(false);

  if (isLoading || state.applications === undefined) {
    return <TableLoader />;
  }

  const hasFiltersApplied =
    table.getState().columnFilters.length > 0 || state.searchString;

  const hasApplicationsOutsidePeriod =
    introducer?.has_application && state.dateFrom !== null;

  const tableStatus = hasFiltersApplied
    ? 'no-results'
    : hasApplicationsOutsidePeriod
      ? 'no-apps-in-period'
      : 'empty';

  const statusConfig = {
    'no-results': {
      icon: <GuyWithLaptopIcon className={styles.icon} />,
      mainText: t`Your search returned no results`,
      extra: (
        <>
          <p className={styles.subText}>
            <Trans>
              Have you tried using different filters and checking for typos to
              see if that solves things?
            </Trans>
          </p>
          <p className={styles.subText}>
            <Trans>
              If you’re still not seeing what you expect,{' '}
              <Button
                variant="tertiary"
                onClick={() => setShowHelpModal(true)}
                className={styles.linkButton}
              >
                contact us
              </Button>{' '}
              for more support?
            </Trans>
          </p>
        </>
      ),
    },
    'no-apps-in-period': {
      icon: <ConfusedGuyLookingAtHisLaptop className={styles.icon} />,
      mainText: t`No applications in this period`,
      extra: (
        <>
          <p className={styles.subText}>
            <Trans>
              To view older applications, adjust the date range in{' '}
              <Button
                variant="tertiary"
                onClick={() => {
                  tracking.ampli.ctaClick({
                    ...getAmplitudeDefaultEventProperties(),
                    'CTA text': 'Dashboard settings',
                    'CTA type': 'Dashboard empty state',
                  });
                  setIsSettingsOpen(true);
                }}
                className={styles.linkButton}
              >
                dashboard settings
              </Button>
              .
            </Trans>
          </p>
          <AddNewApplicationNavItem className={styles.addButton} />
        </>
      ),
    },
    empty: {
      icon: <ConfusedGuyLookingAtHisLaptop className={styles.icon} />,
      mainText: t`No applications yet`,
      extra: (
        <>
          <p className={styles.subText}>
            <Trans>Start creating your first application.</Trans>
          </p>
          <AddNewApplicationNavItem className={styles.addButton} />
        </>
      ),
    },
  } as const;

  const { icon, mainText, extra } = statusConfig[tableStatus];

  return (
    <div data-testid="table-mounted" className={styles.tableStatus}>
      <div className={styles.content}>
        {icon}
        <p className={styles.text}>{mainText}</p>
        {extra}
      </div>
      <HelpModal
        showModal={showHelpModal}
        closeModal={() => setShowHelpModal(false)}
      />
    </div>
  );
};
